import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Instagram, Youtube, Send } from 'lucide-react';
import Logo from '@/components/Logo';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { href: '/about', label: 'About Us' },
    { href: '/courses', label: 'Courses' },
    { href: '/blog', label: 'Blog' },
    { href: '/contact', label: 'Contact' },
  ];

  const legalLinks = [
    { href: '/disclaimer', label: 'Disclaimer' },
    { href: '/privacy-policy', label: 'Privacy Policy' },
    { href: '/terms', label: 'Terms of Service' },
    { href: '/refund-policy', label: 'Refund Policy' },
  ];

  const socialLinks = [
    { href: 'https://www.instagram.com/ratchagananalysis/', icon: Instagram, label: 'Instagram' },
    { href: 'https://www.youtube.com/@ratchagananalysis', icon: Youtube, label: 'YouTube' },
    { href: 'https://t.me/ratchagananalysis', icon: Send, label: 'Telegram' },
  ];

  return (
    <footer className="bg-primary text-primary-foreground relative overflow-hidden bg-gradient-to-b from-primary to-navy-dark pt-20">
      {/* Decorative background element */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent via-accent-light to-accent" />

      <div className="container-max section-padding pb-12 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Logo
              imageClassName="w-16 h-16 md:w-20 md:h-20"
              className="mb-8"
              variant="light"
            />
            <p className="text-xs font-black uppercase tracking-[0.2em] opacity-60 mb-8 leading-loose">
              Study Today<br />
              Trade Tomorrow.
            </p>
            <div className="flex gap-4">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-accent hover:border-accent hover:text-accent-foreground transition-all duration-300 group"
                  aria-label={social.label}
                >
                  <social.icon className="w-4 h-4 transition-transform group-hover:scale-110" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-[0.3em] mb-8 text-accent">Quick Access</h3>
            <ul className="space-y-4">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm font-medium opacity-60 hover:opacity-100 hover:text-accent transition-all flex items-center group"
                  >
                    <span className="w-0 group-hover:w-4 h-px bg-accent mr-0 group-hover:mr-2 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-[0.3em] mb-8 text-accent">Legal Intelligence</h3>
            <ul className="space-y-4">
              {legalLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm font-medium opacity-60 hover:opacity-100 hover:text-accent transition-all flex items-center group"
                  >
                    <span className="w-0 group-hover:w-4 h-px bg-accent mr-0 group-hover:mr-2 transition-all duration-300" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-xs font-black uppercase tracking-[0.3em] mb-8 text-accent">Headquarters</h3>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-4 h-4 text-accent" />
                </div>
                <a
                  href="mailto:ratchagananalysis@gmail.com"
                  className="text-sm font-medium opacity-60 hover:opacity-100 transition-colors pt-1"
                >
                  ratchagananalysis@gmail.com
                </a>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-4 h-4 text-accent" />
                </div>
                <a
                  href="tel:9663306819"
                  className="text-sm font-medium opacity-60 hover:opacity-100 transition-colors pt-1"
                >
                  +91 96633 06819
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-20 pt-12 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">
              © {currentYear} Ratchagan Analysis. All Rights Reserved.
            </p>
            <div className="flex items-center gap-6">
              <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Privacy Protected</span>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] opacity-40">Secure Transactions</span>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-white/5 text-center">
            <p className="text-[11px] font-medium tracking-widest text-[#888888] uppercase">
              Powered by{' '}
              <a
                href="https://gloovup.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent transition-colors duration-300 no-underline"
              >
                Gloov Up Group
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
